import {
  ClinicProduct,
  ClinicProductQuery,
  ListResult,
} from '../types';
import { delay } from './delay';
import { db, findProductById } from './db';
import { ERR_NOT_FOUND } from './errors';

const matchesQuery = (
  product: ClinicProduct,
  query: ClinicProductQuery,
): boolean => {
  if (query.category && product.category !== query.category) return false;
  if (query.herbOrigin && product.herbOrigin !== query.herbOrigin) return false;
  if (query.stockStatus && product.stockStatus !== query.stockStatus) return false;
  if (query.brands && query.brands.length > 0) {
    if (!product.brand || !query.brands.includes(product.brand)) return false;
  }
  if (typeof query.priceMin === 'number' && product.salePrice < query.priceMin) return false;
  if (typeof query.priceMax === 'number' && product.salePrice > query.priceMax) return false;
  if (query.onPromotion && !product.originalPrice) return false;
  if (query.keyword) {
    const keyword = query.keyword.trim().toLowerCase();
    if (keyword.length > 0) {
      const inName = product.nameEn.toLowerCase().includes(keyword);
      const inBrand = (product.brand ?? '').toLowerCase().includes(keyword);
      if (!inName && !inBrand) return false;
    }
  }
  return true;
};

export const productMock = {
  list: async (
    query: ClinicProductQuery = {},
  ): Promise<ListResult<ClinicProduct>> => {
    await delay();
    const skip = query.skip ?? 0;
    const take = query.take ?? 0;
    const filtered = db.products.filter((p) => matchesQuery(p, query));
    const items = take === 0 ? filtered.slice(skip) : filtered.slice(skip, skip + take);
    return { items, skip, take, total: filtered.length };
  },

  detail: async (productId: string): Promise<ClinicProduct> => {
    await delay();
    const product = findProductById(productId);
    if (!product) throw ERR_NOT_FOUND('Product');
    return { ...product };
  },

  facets: async () => {
    await delay();
    const brandCounts = new Map<string, number>();
    let min = Infinity;
    let max = 0;
    for (const p of db.products) {
      if (p.brand) brandCounts.set(p.brand, (brandCounts.get(p.brand) ?? 0) + 1);
      if (p.salePrice < min) min = p.salePrice;
      if (p.salePrice > max) max = p.salePrice;
    }
    const brands = Array.from(brandCounts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
    return {
      brands,
      priceMin: Math.floor(min === Infinity ? 0 : min),
      priceMax: Math.ceil(max),
      items: db.products.map((p) => ({ ...p })),
    };
  },
};
